type MatchableItem = {
  itemId?: string;
  title?: string;
  amount?: number;
  message?: string | null;
  courseType?: { id: string };
  courseId?: string | null;
  isRefunded?: boolean;
  discount?: number;
  variations?: Variation[];
  removedVariations?: Variation[];
  discountId?: string | null;
  discountGroupId?: string | null;
  discountTitle?: string | null;
};

type Variation = { id: string; name: string } | string;

// Flattens variations for comparison
const flattenVariations = (variations: Variation[]) => {
  return variations
    .map((variation) => {
      if (typeof variation === 'string') {
        return variation;
      } else if ('id' in variation) {
        return variation.id;
      }
      return '';
    })
    .sort()
    .join('');
};

// Compare variations
const compareVariations = (a?: Variation[], b?: Variation[]) => {
  if (a == undefined || b == undefined) return true;

  return flattenVariations(a) === flattenVariations(b);
};

// Compare removed variations
const compareRemovedVariations = (a?: Variation[], b?: Variation[]) => {
  if (a == undefined || b == undefined) return true;

  return flattenVariations(a) === flattenVariations(b);
};

// Check if the variations and message is an exact match.
export const exactMatch = (a: MatchableItem, b: MatchableItem) => {
  return (
    a.discountId === b.discountId &&
    a.discountTitle === b.discountTitle &&
    a.itemId === b.itemId &&
    a.title === b.title &&
    a.amount === b.amount &&
    a.message === b.message &&
    (a.courseId === b.courseId || a.courseType?.id === b.courseType?.id) &&
    a.isRefunded === b.isRefunded && // Used only in history views
    // Joar: Two equal signs on purpose here. Null and 0 are equal, in this case.
    a.discount == b.discount &&
    compareVariations(a.variations, b.variations) &&
    compareRemovedVariations(a.removedVariations, b.removedVariations)
  );
};

export type CombinedItem<T extends { variations?: Variation[]; fraction?: number }> = T & {
  count: number;
};

// Generic function to combine items
export const combineArticles = <T extends { variations?: Variation[]; fraction?: number }>(
  items: T[],
): CombinedItem<T>[] => {
  return items.reduce((accumulator: CombinedItem<T>[], currentItem) => {
    // Find an item in the accumulator that matches the current item
    const existingItemIndex = accumulator.findIndex((accItem) => exactMatch(accItem, currentItem));

    if (existingItemIndex !== -1) {
      // If the item already exists, increase its count
      accumulator[existingItemIndex].count +=
        currentItem.fraction !== undefined ? currentItem.fraction : 1;
    } else {
      // If the item does not exist, add it to the accumulator with its count
      const newItem: CombinedItem<T> = {
        ...currentItem,
        count: currentItem.fraction !== undefined ? currentItem.fraction : 1,
      };
      accumulator.push(newItem);
    }

    return accumulator;
  }, []);
};
